// Binary STL export. The panel region (boundary minus holes) is triangulated
// with a constrained Delaunay triangulation (cdt2d, robust predicates),
// extruded to thickness, and written as a watertight mesh: every contour
// edge is a triangulation constraint, side walls are built from the same
// ring vertices, so directed edges pair up exactly. Earcut was rejected
// here: exactly collinear vertices across hole rings (routine in Voronoi
// layouts) break its hole bridging.

import cdt2d from 'cdt2d'
import earcut from 'earcut'
import pc from 'polygon-clipping'
import type { GenResult, Poly } from '../core/types'
import { panelGeometry, normalizePoly, splitCutLines } from '../core/pipeline'
import { ringBBox, signedArea } from '../core/geometry'

interface Tri {
  ax: number; ay: number; az: number
  bx: number; by: number; bz: number
  cx: number; cy: number; cz: number
}

// Above this many outline vertices, one constrained triangulation over the
// whole face turns superlinear and can freeze for tens of seconds. The face
// is then partitioned into grid cells triangulated independently: cut-line
// edges appear in both neighboring cells with opposite directions and cancel,
// so the merged faces remain one valid triangulation of the same region, and
// walls are suppressed on cut lines. The mesh stays a single watertight
// solid; only the face triangulation gains vertices along the cuts.
const CHUNK_VERTEX_THRESHOLD = 30000
const CHUNK_TARGET_VERTS = 1500

export function buildPanelTriangles(result: GenResult, thickness: number): Tri[] {
  const totalVerts =
    result.boundary.length +
    result.holes.reduce((n, h) => n + h.poly.reduce((m, r) => m + r.length, 0), 0)
  const tris: Tri[] = []
  if (totalVerts <= CHUNK_VERTEX_THRESHOLD) {
    for (const poly of panelGeometry(result)) addPolyTriangles(tris, poly, thickness)
    return tris
  }

  // Chunked path. Each cell's face is computed locally, (cell n boundary)
  // minus only the holes whose bounding boxes touch the cell, so every
  // boolean call stays small. Holes spanning a cut line are subtracted in
  // each neighboring cell; both cells cut them with the identical line, so
  // the pieces share vertices exactly and the merged faces stay one valid
  // triangulation of the true panel.
  const bb = ringBBox(result.boundary)
  const bbW = Math.max(bb.maxX - bb.minX, 1e-9)
  const bbH = Math.max(bb.maxY - bb.minY, 1e-9)
  const cellsWanted = Math.ceil(totalVerts / CHUNK_TARGET_VERTS)
  const cols = Math.max(1, Math.round(Math.sqrt((cellsWanted * bbW) / bbH)))
  const rows = Math.max(1, Math.ceil(cellsWanted / cols))
  const cuts = splitCutLines(result.boundary, { cols, rows })
  const onCut = (ax: number, ay: number, bx: number, by: number): boolean => {
    if (Math.abs(ax - bx) < 1e-9) {
      for (const x of cuts.xs) if (Math.abs(ax - x) < 1e-7) return true
    }
    if (Math.abs(ay - by) < 1e-9) {
      for (const y of cuts.ys) if (Math.abs(ay - y) < 1e-7) return true
    }
    return false
  }

  // Bucket holes by the cells their bbox overlaps.
  const cellW = bbW / cols
  const cellH = bbH / rows
  const holeBBs = result.holes.map((h) => ringBBox(h.poly[0]))
  const buckets: number[][] = Array.from({ length: cols * rows }, () => [])
  result.holes.forEach((_, i) => {
    const hb = holeBBs[i]
    const c0 = Math.max(0, Math.floor((hb.minX - bb.minX) / cellW))
    const c1 = Math.min(cols - 1, Math.floor((hb.maxX - bb.minX) / cellW))
    const r0 = Math.max(0, Math.floor((hb.minY - bb.minY) / cellH))
    const r1 = Math.min(rows - 1, Math.floor((hb.maxY - bb.minY) / cellH))
    for (let r = r0; r <= r1; r++) {
      for (let c = c0; c <= c1; c++) buckets[r * cols + c].push(i)
    }
  })

  const boundaryMulti: [number, number][][][] = [[result.boundary as [number, number][]]]
  const EPS = 1e-7
  for (let r = 0; r < rows; r++) {
    for (let c = 0; c < cols; c++) {
      const x0 = bb.minX + cellW * c
      const x1 = bb.minX + cellW * (c + 1)
      const y0 = bb.minY + cellH * r
      const y1 = bb.minY + cellH * (r + 1)
      const cell: [number, number][] = [
        [x0, y0],
        [x1, y0],
        [x1, y1],
        [x0, y1],
      ]
      // Holes strictly inside the cell (and without islands) skip booleans
      // entirely and insert as inner rings verbatim; this keeps their exact
      // vertices (walls and faces always agree) and sidesteps a Martinez
      // degeneracy where collinear flat edges of neighboring clipped holes
      // get merged through a zero-width slit. Only straddlers are clipped.
      const contained: number[] = []
      const straddling: number[] = []
      for (const i of buckets[r * cols + c]) {
        const hb = holeBBs[i]
        const inCell =
          hb.minX > x0 + EPS && hb.maxX < x1 - EPS && hb.minY > y0 + EPS && hb.maxY < y1 - EPS
        if (inCell && result.holes[i].poly.length === 1) contained.push(i)
        else straddling.push(i)
      }
      let face: [number, number][][][]
      try {
        face = pc.intersection(boundaryMulti, [[cell]])
        if (face.length > 0 && straddling.length > 0) {
          // One MultiPolygon argument = one boolean pass for all straddlers.
          const holesMulti = straddling.map((i) => result.holes[i].poly as [number, number][][])
          face = pc.difference(face, holesMulti)
        }
      } catch {
        continue // a degenerate cell never kills the export
      }
      const polys: Poly[] = []
      for (const poly of face) {
        const norm = normalizePoly(poly as Poly)
        if (norm) polys.push(norm)
      }
      for (const i of contained) {
        const outer = result.holes[i].poly[0]
        // Attach to the face poly that contains the hole (usually the only one).
        const target =
          polys.length === 1
            ? polys[0]
            : polys.find((p) => pointInRingFast(outer[0], p[0])) ?? polys[0]
        if (target) target.push([...outer].reverse()) // holes wind CW
      }
      for (const poly of polys) addPolyTrianglesFast(tris, poly, thickness, onCut)
    }
  }
  return tris
}

// Conforming pass over face triangles: a triangulator may legally emit an
// interior edge spanning several collinear constraint vertices of OTHER
// rings (clipped rim holes put their flat corners on one shared line), while
// the far side of that line is segmented at those corners. Area is exact but
// welded edge pairing breaks (a T-junction). This pass splits any triangle
// edge at ring vertices lying on it, one edge per step (the fan from the
// opposite vertex is then never degenerate), until the triangulation
// conforms to every ring vertex.
function splitTJunctions(points: [number, number][], triangles: number[][]): number[][] {
  // Spatial hash of all ring vertices.
  const CELL = 2
  const grid = new Map<string, number[]>()
  const key = (gx: number, gy: number) => `${gx},${gy}`
  points.forEach(([x, y], i) => {
    const k = key(Math.floor(x / CELL), Math.floor(y / CELL))
    const list = grid.get(k)
    if (list) list.push(i)
    else grid.set(k, [i])
  })

  const out: number[][] = []
  const queue: number[][] = triangles.slice()
  const EPS_DIST = 1e-6
  while (queue.length > 0) {
    const tri = queue.pop()!
    let split = false
    for (let e = 0; e < 3 && !split; e++) {
      const ia = tri[e]
      const ib = tri[(e + 1) % 3]
      const ic = tri[(e + 2) % 3]
      const [ax, ay] = points[ia]
      const [bx, by] = points[ib]
      const dx = bx - ax
      const dy = by - ay
      const len2 = dx * dx + dy * dy
      if (len2 < 1e-18) continue
      // Gather candidate vertices in the edge's bbox neighborhood.
      const gx0 = Math.floor((Math.min(ax, bx) - EPS_DIST) / CELL)
      const gx1 = Math.floor((Math.max(ax, bx) + EPS_DIST) / CELL)
      const gy0 = Math.floor((Math.min(ay, by) - EPS_DIST) / CELL)
      const gy1 = Math.floor((Math.max(ay, by) + EPS_DIST) / CELL)
      const mids: { i: number; t: number }[] = []
      for (let gy = gy0; gy <= gy1; gy++) {
        for (let gx = gx0; gx <= gx1; gx++) {
          for (const vi of grid.get(key(gx, gy)) ?? []) {
            if (vi === ia || vi === ib || vi === ic) continue
            const [px, py] = points[vi]
            const t = ((px - ax) * dx + (py - ay) * dy) / len2
            if (t <= 1e-9 || t >= 1 - 1e-9) continue
            const distNum = Math.abs(dx * (py - ay) - dy * (px - ax))
            if (distNum / Math.sqrt(len2) > EPS_DIST) continue
            mids.push({ i: vi, t })
          }
        }
      }
      if (mids.length === 0) continue
      mids.sort((m, n) => m.t - n.t)
      // Fan from the opposite vertex across the subdivided edge.
      const chain = [ia, ...mids.map((m) => m.i), ib]
      for (let i = 0; i + 1 < chain.length; i++) {
        queue.push([chain[i], chain[i + 1], ic])
      }
      split = true
    }
    if (!split) out.push(tri)
  }
  return out
}

function pointInRingFast(p: [number, number], ring: [number, number][]): boolean {
  let inside = false
  const [px, py] = p
  for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
    const [xi, yi] = ring[i]
    const [xj, yj] = ring[j]
    if (yi > py !== yj > py && px < ((xj - xi) * (py - yi)) / (yj - yi) + xi) inside = !inside
  }
  return inside
}

// Fast face triangulation for chunked cells: earcut, accepted only when its
// SIGNED area sum reproduces the face area exactly and no output triangle is
// truly zero-area (earcut's historical failure modes here are fold-overs and
// dropped regions). Only then does the cell fall back to cdt2d; note that
// cdt2d itself has been observed to miscover chunk-scale cells whose
// constraints include collinear clipped-hole flats (valid total area, missing
// strip plus a compensating double cover), so the checked earcut result is
// the primary path and the threshold for rejecting it is deliberately tight.
function addPolyTrianglesFast(
  tris: Tri[],
  poly: Poly,
  th: number,
  skipWall: (ax: number, ay: number, bx: number, by: number) => boolean,
) {
  const flat: number[] = []
  const holeIdx: number[] = []
  for (let r = 0; r < poly.length; r++) {
    if (r > 0) holeIdx.push(flat.length / 2)
    for (const [x, y] of poly[r]) flat.push(x, y)
  }
  const idx = earcut(flat, holeIdx.length ? holeIdx : undefined)

  // The SIGNED triangle area sum must reproduce the face area exactly (up
  // to float noise). Earcut's failure modes on collinear hole corners (the
  // reason v1 rejected it) are fold-overs and dropped regions; a fold-over
  // hides from an absolute-area check because the folded overlap exactly
  // compensates the missing strip, but its negative winding cannot hide
  // from the signed sum. Degenerate slivers are rejected outright. Either
  // symptom sends the cell to cdt2d's robust predicates.
  let faceArea = Math.abs(signedArea(poly[0]))
  for (let r = 1; r < poly.length; r++) faceArea -= Math.abs(signedArea(poly[r]))
  let signedSum = 0
  let degenerate = false
  for (let i = 0; i < idx.length; i += 3) {
    const ax = flat[idx[i] * 2]
    const ay = flat[idx[i] * 2 + 1]
    const bx = flat[idx[i + 1] * 2]
    const by = flat[idx[i + 1] * 2 + 1]
    const cx = flat[idx[i + 2] * 2]
    const cy = flat[idx[i + 2] * 2 + 1]
    const cross = (bx - ax) * (cy - ay) - (by - ay) * (cx - ax)
    if (Math.abs(cross) < 1e-12) degenerate = true
    signedSum += cross / 2
  }
  if (degenerate || Math.abs(Math.abs(signedSum) - faceArea) > Math.max(1e-6, faceArea * 1e-6)) {
    addPolyTriangles(tris, poly, th, skipWall)
    return
  }

  const pts2: [number, number][] = []
  for (let i = 0; i < flat.length; i += 2) pts2.push([flat[i], flat[i + 1]])
  const triIdx: number[][] = []
  for (let i = 0; i < idx.length; i += 3) triIdx.push([idx[i], idx[i + 1], idx[i + 2]])
  emitFaces(tris, pts2, splitTJunctions(pts2, triIdx), th)

  for (const ring of poly) {
    const n = ring.length
    for (let i = 0; i < n; i++) {
      const [ax, ay] = ring[i]
      const [bx, by] = ring[(i + 1) % n]
      if (skipWall(ax, ay, bx, by)) continue
      tris.push({ ax, ay, az: 0, bx, by, bz: 0, cx: bx, cy: by, cz: th })
      tris.push({ ax, ay, az: 0, bx, by, bz: th, cx: ax, cy: ay, cz: th })
    }
  }
}

// Top and bottom faces from conforming 2D triangles.
function emitFaces(tris: Tri[], points: [number, number][], triangles: number[][], th: number) {
  for (const [a, b, c] of triangles) {
    const [ax, ay] = points[a]
    const [bx, by] = points[b]
    const [cx, cy] = points[c]
    const cross = (bx - ax) * (cy - ay) - (by - ay) * (cx - ax)
    if (cross >= 0) {
      tris.push({ ax, ay, az: th, bx, by, bz: th, cx, cy, cz: th })
      tris.push({ ax, ay, az: 0, bx: cx, by: cy, bz: 0, cx: bx, cy: by, cz: 0 })
    } else {
      tris.push({ ax, ay, az: th, bx: cx, by: cy, bz: th, cx: bx, cy: by, cz: th })
      tris.push({ ax, ay, az: 0, bx, by, bz: 0, cx, cy, cz: 0 })
    }
  }
}

function addPolyTriangles(
  tris: Tri[],
  poly: Poly,
  th: number,
  skipWall?: (ax: number, ay: number, bx: number, by: number) => boolean,
) {
  // Build a point list (deduplicated by exact coordinates) and constrain
  // every ring edge, then triangulate the interior only.
  const points: [number, number][] = []
  const index = new Map<string, number>()
  const edges: [number, number][] = []
  const idOf = (x: number, y: number): number => {
    const k = `${x},${y}`
    let i = index.get(k)
    if (i === undefined) {
      i = points.length
      points.push([x, y])
      index.set(k, i)
    }
    return i
  }
  for (const ring of poly) {
    const ids = ring.map(([x, y]) => idOf(x, y))
    for (let i = 0; i < ids.length; i++) {
      const a = ids[i]
      const b = ids[(i + 1) % ids.length]
      if (a !== b) edges.push([a, b])
    }
  }
  const indices = cdt2d(points, edges, { exterior: false })
  emitFaces(tris, points, splitTJunctions(points, indices), th)

  // Side walls. For outer rings (CCW) and hole rings (CW) alike, the quad
  // (a0, b0, b1, a1) faces outward from the solid.
  for (const ring of poly) {
    const n = ring.length
    for (let i = 0; i < n; i++) {
      const [ax, ay] = ring[i]
      const [bx, by] = ring[(i + 1) % n]
      if (skipWall && skipWall(ax, ay, bx, by)) continue
      tris.push({ ax, ay, az: 0, bx, by, bz: 0, cx: bx, cy: by, cz: th })
      tris.push({ ax, ay, az: 0, bx, by, bz: th, cx: ax, cy: ay, cz: th })
    }
  }
}

export function exportStl(result: GenResult, thickness: number): ArrayBuffer {
  const tris = buildPanelTriangles(result, thickness)
  const buffer = new ArrayBuffer(84 + tris.length * 50)
  const view = new DataView(buffer)
  const header = 'Ventholes panel, binary STL, millimetres'
  for (let i = 0; i < Math.min(80, header.length); i++) view.setUint8(i, header.charCodeAt(i))
  view.setUint32(80, tris.length, true)
  let off = 84
  for (const t of tris) {
    // Facet normal.
    const ux = t.bx - t.ax, uy = t.by - t.ay, uz = t.bz - t.az
    const vx = t.cx - t.ax, vy = t.cy - t.ay, vz = t.cz - t.az
    let nx = uy * vz - uz * vy
    let ny = uz * vx - ux * vz
    let nz = ux * vy - uy * vx
    const len = Math.hypot(nx, ny, nz) || 1
    nx /= len; ny /= len; nz /= len
    view.setFloat32(off, nx, true)
    view.setFloat32(off + 4, ny, true)
    view.setFloat32(off + 8, nz, true)
    view.setFloat32(off + 12, t.ax, true)
    view.setFloat32(off + 16, t.ay, true)
    view.setFloat32(off + 20, t.az, true)
    view.setFloat32(off + 24, t.bx, true)
    view.setFloat32(off + 28, t.by, true)
    view.setFloat32(off + 32, t.bz, true)
    view.setFloat32(off + 36, t.cx, true)
    view.setFloat32(off + 40, t.cy, true)
    view.setFloat32(off + 44, t.cz, true)
    view.setUint16(off + 48, 0, true)
    off += 50
  }
  return buffer
}
